/**
 * API Error class with code, message, and details
 * Matches the Worker API error response format
 */
export class ApiError extends Error {
  code: string;
  details?: unknown;
  requestId?: string;
  statusCode: number;

  constructor(
    statusCode: number,
    code: string,
    message: string,
    details?: unknown,
    requestId?: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
}

/**
 * Error codes from the Worker API
 */
export const ErrorCodes = {
  // General
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  BAD_REQUEST: 'BAD_REQUEST',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  RATE_LIMITED: 'RATE_LIMITED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',

  // BigCommerce
  BIGCOMMERCE_ERROR: 'BIGCOMMERCE_ERROR',
  CHANNEL_1_NOT_ALLOWED: 'CHANNEL_1_NOT_ALLOWED',
  NO_PICKUP_METHOD: 'NO_PICKUP_METHOD',
  CHANNEL_NOT_FOUND: 'CHANNEL_NOT_FOUND',
  LOCATION_NOT_FOUND: 'LOCATION_NOT_FOUND',
  PRODUCT_NOT_FOUND: 'PRODUCT_NOT_FOUND',
  INVENTORY_INSUFFICIENT: 'INVENTORY_INSUFFICIENT',

  // Payments
  STRIPE_ERROR: 'STRIPE_ERROR',
  STRIPE_NOT_CONFIGURED: 'STRIPE_NOT_CONFIGURED',
  PAYMENT_FAILED: 'PAYMENT_FAILED',
  READER_NOT_CONNECTED: 'READER_NOT_CONNECTED',

  // Communications
  TWILIO_ERROR: 'TWILIO_ERROR',
  SENDGRID_ERROR: 'SENDGRID_ERROR',

  // Device/Staff
  DEVICE_NOT_FOUND: 'DEVICE_NOT_FOUND',
  DEVICE_ALREADY_PAIRED: 'DEVICE_ALREADY_PAIRED',
  PAIRING_CODE_EXPIRED: 'PAIRING_CODE_EXPIRED',
  PAIRING_CODE_INVALID: 'PAIRING_CODE_INVALID',
  STAFF_NOT_FOUND: 'STAFF_NOT_FOUND',
  INVALID_PIN: 'INVALID_PIN',
  OTP_REQUIRED: 'OTP_REQUIRED',
  OTP_INVALID: 'OTP_INVALID',

  // Subscription
  SUBSCRIPTION_REQUIRED: 'SUBSCRIPTION_REQUIRED',
  SUBSCRIPTION_LIMIT_REACHED: 'SUBSCRIPTION_LIMIT_REACHED',
  FEATURE_NOT_AVAILABLE: 'FEATURE_NOT_AVAILABLE',
} as const;

export type ErrorCode = (typeof ErrorCodes)[keyof typeof ErrorCodes];

/**
 * User-friendly error message with optional action
 */
export interface FriendlyError {
  title: string;
  message: string;
  action?: {
    label: string;
    path?: string;
    onClick?: () => void;
  };
}

/**
 * User-friendly error messages for common error codes
 */
const friendlyErrors: Record<string, FriendlyError> = {
  // General errors
  [ErrorCodes.VALIDATION_ERROR]: {
    title: 'Invalid Input',
    message: 'Please check your input and try again.',
  },
  [ErrorCodes.BAD_REQUEST]: {
    title: 'Invalid Request',
    message: 'The request could not be processed. Please try again.',
  },
  [ErrorCodes.UNAUTHORIZED]: {
    title: 'Session Expired',
    message: 'Your session has expired. Please refresh the page to continue.',
    action: { label: 'Refresh Page', onClick: () => window.location.reload() },
  },
  [ErrorCodes.FORBIDDEN]: {
    title: 'Access Denied',
    message: 'You do not have permission to perform this action.',
  },
  [ErrorCodes.NOT_FOUND]: {
    title: 'Not Found',
    message: 'The requested resource was not found.',
  },
  [ErrorCodes.CONFLICT]: {
    title: 'Conflict',
    message: 'This action conflicts with existing data.',
  },
  [ErrorCodes.RATE_LIMITED]: {
    title: 'Too Many Requests',
    message: 'BigCommerce is rate limiting requests. Please wait a moment and try again.',
  },
  [ErrorCodes.INTERNAL_ERROR]: {
    title: 'Server Error',
    message: 'An unexpected error occurred. Our team has been notified.',
  },

  // BigCommerce errors
  [ErrorCodes.BIGCOMMERCE_ERROR]: {
    title: 'BigCommerce Error',
    message: 'Failed to communicate with BigCommerce. Please try again.',
  },
  [ErrorCodes.CHANNEL_1_NOT_ALLOWED]: {
    title: 'Invalid Channel',
    message: 'Channel 1 is your storefront and cannot be used for POS. Please select or create a dedicated POS channel.',
    action: { label: 'Go to Settings', path: '/settings' },
  },
  [ErrorCodes.NO_PICKUP_METHOD]: {
    title: 'Pickup Method Required',
    message: 'This location needs a pickup method configured in BigCommerce before it can be enabled for POS.',
    action: { label: 'Configure in BigCommerce' },
  },
  [ErrorCodes.CHANNEL_NOT_FOUND]: {
    title: 'Channel Not Found',
    message: 'The POS channel could not be found. It may have been deleted in BigCommerce.',
    action: { label: 'Go to Settings', path: '/settings' },
  },
  [ErrorCodes.LOCATION_NOT_FOUND]: {
    title: 'Location Not Found',
    message: 'The selected location could not be found. It may have been removed.',
    action: { label: 'Go to Locations', path: '/locations' },
  },
  [ErrorCodes.PRODUCT_NOT_FOUND]: {
    title: 'Product Not Found',
    message: 'The product could not be found. It may have been deleted or is not visible on your POS channel.',
  },
  [ErrorCodes.INVENTORY_INSUFFICIENT]: {
    title: 'Insufficient Inventory',
    message: 'There is not enough inventory at this location to complete the sale.',
  },

  // Payment errors
  [ErrorCodes.STRIPE_ERROR]: {
    title: 'Payment Error',
    message: 'An error occurred while processing the payment. Please check your Stripe configuration.',
    action: { label: 'Go to Payments', path: '/payments' },
  },
  [ErrorCodes.STRIPE_NOT_CONFIGURED]: {
    title: 'Stripe Not Configured',
    message: 'Stripe Terminal is not configured. Please add your Stripe credentials to accept card payments.',
    action: { label: 'Configure Stripe', path: '/payments' },
  },
  [ErrorCodes.PAYMENT_FAILED]: {
    title: 'Payment Failed',
    message: 'The payment could not be processed. Please try again or use a different payment method.',
  },
  [ErrorCodes.READER_NOT_CONNECTED]: {
    title: 'Reader Not Connected',
    message: 'No card reader is connected. Please connect a reader or use Tap to Pay on your device.',
  },

  // Communication errors
  [ErrorCodes.TWILIO_ERROR]: {
    title: 'SMS Error',
    message: 'Failed to send SMS. Please check your Twilio configuration.',
    action: { label: 'Check Twilio Settings' },
  },
  [ErrorCodes.SENDGRID_ERROR]: {
    title: 'Email Error',
    message: 'Failed to send email. Please check your SendGrid configuration.',
    action: { label: 'Check SendGrid Settings' },
  },

  // Device/Staff errors
  [ErrorCodes.DEVICE_NOT_FOUND]: {
    title: 'Device Not Found',
    message: 'This device is not registered. It may have been removed from the admin.',
    action: { label: 'Go to Devices', path: '/devices' },
  },
  [ErrorCodes.DEVICE_ALREADY_PAIRED]: {
    title: 'Device Already Paired',
    message: 'This device is already paired with another account.',
  },
  [ErrorCodes.PAIRING_CODE_EXPIRED]: {
    title: 'Pairing Code Expired',
    message: 'The pairing code has expired. Please generate a new one.',
    action: { label: 'Generate New Code' },
  },
  [ErrorCodes.PAIRING_CODE_INVALID]: {
    title: 'Invalid Pairing Code',
    message: 'The pairing code is invalid. Please check the code and try again.',
  },
  [ErrorCodes.STAFF_NOT_FOUND]: {
    title: 'Staff Not Found',
    message: 'This staff member was not found. They may have been removed.',
    action: { label: 'Go to Staff', path: '/staff' },
  },
  [ErrorCodes.INVALID_PIN]: {
    title: 'Invalid PIN',
    message: 'The PIN entered is incorrect. Please try again.',
  },
  [ErrorCodes.OTP_REQUIRED]: {
    title: 'Verification Required',
    message: 'This action requires manager verification. Please enter the OTP.',
  },
  [ErrorCodes.OTP_INVALID]: {
    title: 'Invalid Code',
    message: 'The verification code is incorrect or has expired.',
  },

  // Subscription errors
  [ErrorCodes.SUBSCRIPTION_REQUIRED]: {
    title: 'Subscription Required',
    message: 'An active subscription is required to use this feature.',
    action: { label: 'View Plans', path: '/billing' },
  },
  [ErrorCodes.SUBSCRIPTION_LIMIT_REACHED]: {
    title: 'Limit Reached',
    message: 'You have reached the limit for your current plan. Upgrade to add more.',
    action: { label: 'Upgrade Plan', path: '/billing' },
  },
  [ErrorCodes.FEATURE_NOT_AVAILABLE]: {
    title: 'Feature Not Available',
    message: 'This feature is not available on your current plan.',
    action: { label: 'View Plans', path: '/billing' },
  },
};

/**
 * Get enhanced error info with title, message, and action
 */
export function getFriendlyError(error: unknown): FriendlyError {
  if (error instanceof ApiError) {
    // Check for specific error code first
    const friendlyError = friendlyErrors[error.code];
    if (friendlyError) {
      // Use API message if it's more specific than the generic one
      const message = error.message && error.message !== error.code && error.message.length > 10
        ? error.message
        : friendlyError.message;
      return { ...friendlyError, message };
    }

    // Fall back to generic error with API message
    return {
      title: 'Error',
      message: error.message || 'An unexpected error occurred.',
    };
  }

  if (error instanceof Error) {
    return {
      title: 'Error',
      message: error.message || 'An unexpected error occurred.',
    };
  }

  if (typeof error === 'string') {
    return {
      title: 'Error',
      message: error,
    };
  }

  return {
    title: 'Error',
    message: 'An unexpected error occurred.',
  };
}

/**
 * Get a user-friendly error message (simple string)
 */
export function getUserFriendlyMessage(error: unknown): string {
  return getFriendlyError(error).message;
}

/**
 * Check if an error is a specific error code
 */
export function isErrorCode(error: unknown, code: ErrorCode): boolean {
  return error instanceof ApiError && error.code === code;
}

/**
 * Check if an error is a validation error
 */
export function isValidationError(error: unknown): error is ApiError {
  return isErrorCode(error, ErrorCodes.VALIDATION_ERROR);
}

/**
 * Check if an error is a not found error
 */
export function isNotFoundError(error: unknown): error is ApiError {
  return isErrorCode(error, ErrorCodes.NOT_FOUND);
}

/**
 * Check if an error is a conflict error
 */
export function isConflictError(error: unknown): error is ApiError {
  return isErrorCode(error, ErrorCodes.CONFLICT);
}

/**
 * Extract validation field errors from ApiError details
 */
export function getValidationErrors(error: ApiError): Record<string, string[]> | null {
  if (error.code !== ErrorCodes.VALIDATION_ERROR || !error.details) {
    return null;
  }

  // Zod error format from API
  if (typeof error.details === 'object' && error.details !== null) {
    const details = error.details as Record<string, unknown>;
    if (Array.isArray(details.fieldErrors)) {
      return details.fieldErrors as unknown as Record<string, string[]>;
    }
    if (typeof details.fieldErrors === 'object') {
      return details.fieldErrors as Record<string, string[]>;
    }
  }

  return null;
}
